import { create } from 'zustand';
import { format } from 'date-fns';
import { i18n } from '@/shared/i18n';

export interface Question {
  id: number;
  text: string;
}

interface FocusGroupFormState {
  title: string;
  /** Whether the person has typed a title, so a late-arriving prefill cannot overwrite it. */
  titleEdited: boolean;
  topic: string;
  brandName: string;
  /** IQ analysis this focus group was started from; null when started outside a report. */
  analysisId: string | null;
  selectedPersonaIds: string[];
  activeStep: number;
  generatedQuestions: Question[];
  setTitle: (title: string) => void;
  prefillTitle: (title: string) => void;
  setAnalysisId: (analysisId: string | null) => void;
  setTopic: (topic: string) => void;
  setBrandName: (brandName: string) => void;
  addPersonaId: (id: string) => void;
  removePersonaId: (id: string) => void;
  setSelectedPersonaIds: (ids: string[]) => void;
  setActiveStep: (step: number) => void;
  setGeneratedQuestions: (questions: string[]) => void;
  updateQuestion: (index: number, value: string) => void;
  addQuestion: () => void;
  removeQuestion: (index: number) => void;
  reset: () => void;
}

let nextQuestionId = 0;

const getDefaultTitle = () =>
  i18n.t('audiences:focusGroup.focusGroupFormStore.defaultTitle', {
    date: format(new Date(), 'MMM d, yyyy'),
  });

const initialState = () => ({
  title: getDefaultTitle(),
  titleEdited: false,
  topic: '',
  brandName: '',
  analysisId: null as string | null,
  selectedPersonaIds: [] as string[],
  activeStep: 0,
  generatedQuestions: [] as Question[],
});

export const useFocusGroupFormStore = create<FocusGroupFormState>((set) => ({
  ...initialState(),
  setTitle: (title) => set({ title, titleEdited: true }),
  prefillTitle: (title) => set((state) => (state.titleEdited ? state : { title })),
  setAnalysisId: (analysisId) => set({ analysisId }),
  setTopic: (topic) => set({ topic }),
  setBrandName: (brandName) => set({ brandName }),
  addPersonaId: (id) =>
    set((state) => ({
      selectedPersonaIds: state.selectedPersonaIds.includes(id)
        ? state.selectedPersonaIds
        : [...state.selectedPersonaIds, id],
    })),
  removePersonaId: (id) =>
    set((state) => ({
      selectedPersonaIds: state.selectedPersonaIds.filter((pid) => pid !== id),
    })),
  setSelectedPersonaIds: (ids) => set({ selectedPersonaIds: ids }),
  setActiveStep: (step) => set({ activeStep: step }),
  setGeneratedQuestions: (questions) =>
    set({
      generatedQuestions: questions.map((text) => ({ id: nextQuestionId++, text })),
    }),
  updateQuestion: (index, value) =>
    set((state) => ({
      generatedQuestions: state.generatedQuestions.map((q, i) =>
        i === index ? { ...q, text: value } : q,
      ),
    })),
  addQuestion: () =>
    set((state) => ({
      generatedQuestions: [...state.generatedQuestions, { id: nextQuestionId++, text: '' }],
    })),
  removeQuestion: (index) =>
    set((state) => ({
      generatedQuestions: state.generatedQuestions.filter((_, i) => i !== index),
    })),
  reset: () => {
    nextQuestionId = 0;
    set(initialState());
  },
}));

export const selectIsSetupValid = (state: FocusGroupFormState) =>
  state.topic.trim().length > 0 && state.selectedPersonaIds.length > 0;

export const selectIsReviewValid = (state: FocusGroupFormState) =>
  state.generatedQuestions.length > 0 &&
  state.generatedQuestions.every((q) => q.text.trim().length > 0);
